/**
 * PostHog initialization for the admin SPA. Mirror of the web app's
 * lib/posthog.ts so admin actions land in the same PostHog project,
 * tagged `surface: admin` (via super-properties) so they're filterable
 * from the public web surface.
 *
 * Differences from the web init:
 *   - No consent gate — admin users are the operator + support team;
 *     analytics consent doesn't apply, and we want every action
 *     captured for support-quality + audit cross-reference.
 *     `opt_out_capturing_by_default: false` flips the opposite of
 *     the public surface.
 *   - Session recording disabled. Admin sessions touch real user PII
 *     (dossier views, photo URLs); we don't want that in playback.
 *   - `person_profiles: 'always'` — every admin is identified via
 *     the bearer once they sign in, so anonymous capture isn't useful
 *     and adds noise.
 *
 * Env vars read (Vite exposes `VITE_*` to the bundle):
 *   VITE_POSTHOG_KEY        required — disables init when missing
 *   VITE_POSTHOG_HOST       defaults to https://eu.i.posthog.com
 *   VITE_POSTHOG_ENVIRONMENT  defaults to VITE_ENV or "development"
 */

import { scrubPii } from '@funkihoo/shared';
import posthog from 'posthog-js';

let initialized = false;

export function initAdminPostHog(): boolean {
  if (initialized) return true;
  const key = import.meta.env['VITE_POSTHOG_KEY'];
  if (!key) return false;

  const host = import.meta.env['VITE_POSTHOG_HOST'] ?? 'https://eu.i.posthog.com';

  posthog.init(key, {
    api_host: host,
    enable_recording_console_log: false,
    person_profiles: 'always',
    // Admin operators consent implicitly by signing in — capture on.
    opt_out_capturing_by_default: false,
    // Admin sessions handle PII; no session recording.
    disable_session_recording: true,
    sanitize_properties: (properties) => scrubPii(properties) as Record<string, unknown>,
  });

  const environment =
    import.meta.env['VITE_POSTHOG_ENVIRONMENT'] ??
    import.meta.env['VITE_ENV'] ??
    (import.meta.env.DEV ? 'development' : 'production');
  posthog.register({
    environment,
    surface: 'admin',
    ...(import.meta.env['VITE_SENTRY_RELEASE']
      ? { release: import.meta.env['VITE_SENTRY_RELEASE'] }
      : {}),
  });

  initialized = true;
  return true;
}

/** Identify the signed-in admin to PostHog. Use the admin id (not
 *  email) so the distinct_id stays stable across email changes. */
export function identifyAdminPostHog(adminId: string): void {
  if (!initialized) return;
  posthog.identify(adminId);
}

/** Capture an admin event. Use canonical names: `admin.<surface>.<verb>`
 *  e.g. `admin.dossier.opened`, `admin.user.banned`, `admin.report.resolved`. */
export function trackAdminEvent(name: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(name, props);
}

/** Reset the local PostHog state on sign-out. */
export function resetAdminPostHog(): void {
  if (!initialized) return;
  posthog.reset();
}
